import { COMMON_CLASS_NAMES } from '../constants';
import { DomAnimation } from '../utils/domAnimation';

export class Tab {
  /** @type {Element} */
  tab;
  controls = new Set();
  _isOpen = false;
  constructor(el) {
    this.tab = el;

    const ariaControls = this.tab.getAttribute('aria-controls');
    if (ariaControls) {
      ariaControls.split(/\s/).forEach(id => {
        const controlEl = document.getElementById(id);
        controlEl && this.controls.add(controlEl);
      });
    }

    this.isOpen = this.tab.getAttribute('aria-selected') === "true";
  }

  /**
   * Checks if the tab is currently open (selected).
   *
   * @returns {boolean} Returns true if the tab's 'aria-selected' attribute is "true", otherwise false.
   */
  get isOpen() {
    return this._isOpen;
  }

  set isOpen(boolean) {
    return this._isOpen = boolean;
  }

  onClick(e) {
    e.stopPropagation();
    this.toggle(!this.isOpen)
  }

  /**
   * Toggles the open/closed state of a tab and its associated controls with animations.
   *
   * @async
   * @param {string | null} animationType
   * @param {boolean} open - If true, opens the tab and shows controls; if false, closes the tab and hides controls.
   * @returns {Promise<void>} Resolves when all animations are complete.
   */
  async toggle(open, animationType) {
    this.transitionState = "running";
    const animation = new DomAnimation();
    const animations = [];

    this.tab.setAttribute('tabindex', Number(open) - 1);
    this.tab.setAttribute("aria-selected", open.toString());
    this.tab.classList.toggle(COMMON_CLASS_NAMES.ACTIVE, open);

    if (open) {
      this.controls.forEach((control) => {
        const animPromiseOpen = new Promise(function(resolve) {
          if(animationType) {
            control.toggleAttribute('hidden', !open);
            control.classList.add(COMMON_CLASS_NAMES.ACTIVE);
            switch (animationType) {
              case DomAnimation.ANIMATION_TYPE.FADE_IN_OUT:
                animationType = DomAnimation.ANIMATION_TYPE.FADE_IN;
                break;
              case DomAnimation.ANIMATION_TYPE.SLIDE_IN_OUT:
                animationType = DomAnimation.ANIMATION_TYPE.SLIDE_IN;
                break;
              default:
                break;
            }
            animation.animate(control, animationType, {duration: 500}, () => {
              return resolve();
            });
          } else {
            requestAnimationFrame(function() {
              control.toggleAttribute('hidden', !open);
              control.classList.add(COMMON_CLASS_NAMES.ACTIVE);
              return resolve();
            })
          }
        });
        animations.push(animPromiseOpen);
      });
    } else {
      this.controls.forEach(control => {
        const animPromiseClose = new Promise(function(resolve) {
          if(animationType) {
            switch (animationType) {
              case DomAnimation.ANIMATION_TYPE.FADE_IN_OUT:
                animationType = DomAnimation.ANIMATION_TYPE.FADE_OUT;
                break;
              case DomAnimation.ANIMATION_TYPE.SLIDE_IN_OUT:
                animationType = DomAnimation.ANIMATION_TYPE.SLIDE_OUT;
                break;
              default:
                break;
            }
            animation.animate(control, animationType, {duration: 500}, () => {
              control.toggleAttribute('hidden', !open);
              control.classList.remove(COMMON_CLASS_NAMES.ACTIVE);
              return resolve();
            });
          } else {
            requestAnimationFrame(() => {
              control.toggleAttribute('hidden', !open);
              control.classList.remove(COMMON_CLASS_NAMES.ACTIVE);
              return resolve();
            })
          }
        });
        animations.push(animPromiseClose);
      });
    }
    await Promise.all(animations).finally(() => {
      this.transitionState = "";
      this.isOpen = open;
    });
  }

  async open(animationType = null) {
    await this.toggle(true, animationType);
  }

  async close(animationType = null) {
    await this.toggle(false, animationType);
  }
}

export class TabManager {
  /** @type {Element} container */
  container;
  /** @type {Set<Tab>} tabs */
  tabs = new Set();
  /** @type {string | null} */
  animationType = null;
  /** @type {Tab | null} */
  _currentTab = null;

  /**
   * An object representing the media query breakpoints for a dropdown.
   * @property {number|undefined} max - The maximum width for the media query. Undefined if not set.
   * @property {number|undefined} min - The minimum width for the media query. Undefined if not set.
   */
  dropdownMediaQuery = {
    max: undefined,
    min: undefined
  }

  constructor(el) {
    this.container = el;
    const tabRole = this.container.querySelectorAll('[role="tab"]');
    const dataAnimationType = this.container.dataset.animationType;
    this.animationType =  Object.values(DomAnimation.ANIMATION_TYPE).includes(dataAnimationType) ? dataAnimationType : null ;
    tabRole.forEach(tabEl => {
      const tab = new Tab(tabEl);
      this.tabs.add(tab);
    })

    /** get the media query breakpoints */
    this.dropdownMediaQuery = {
      max: this.container.dataset.dropdownMediaQueryMax,
      min: this.container.dataset.dropdownMediaQueryMin
    };

    this.currentTab = Array.from(this.tabs).find(tab => tab.isOpen === true);
    this.listen();
  }

  static CLASS_NAMES = {
    TABLIST: "js-tablist"
  }

  /**
   * @method handleResize
   * Handles the resize event and updates the container's class
   * based on the availability status.
   *
   */
  handleResize() {
    if (this.isAvailable()) {
      this.tabs.forEach(tab => tab.tab.classList.add(COMMON_CLASS_NAMES.AVAILABLE));
    } else {
      this.tabs.forEach(tab => tab.tab.classList.remove(COMMON_CLASS_NAMES.AVAILABLE));
    }
  }

  /**
   * Determines if the current window width falls within the specified media query range.
   *
   * @returns {boolean} - Returns `true` if the window width is within the range defined
   *                      by `dropdownMediaQuery.min` and `dropdownMediaQuery.max`, otherwise `false`.
   */
  isAvailable() {
    /** @type {boolean} */
    let result = true;
    if (this.dropdownMediaQuery.max != null) {
      result = result && window.innerWidth < this.dropdownMediaQuery.max;
    }
    if (this.dropdownMediaQuery.min != null) {
      result = result && window.innerWidth > this.dropdownMediaQuery.min;
    }
    return result;
  }

  get currentTab() {
    return this._currentTab;
  }

  set currentTab(el) {
    return this._currentTab = el;
  }

  listen() {
    this.tabs.forEach(tab => {
      tab.tab.addEventListener('click',  this.changeTabs.bind(this, tab));
    })
    this.handleResize();
    if (Object.values(this.dropdownMediaQuery).some(val => val != null)) {
      window.addEventListener('resize', this.handleResize.bind(this));
    }
  }

  /**
   * Handles switching between tabs by closing the currently open tab (if any)
   * and toggling the selected tab's open state. Updates the reference to the current tab.
   *
   * @async
   * @param {Tab} tab - The tab object to switch to.
   * @param {Function} tab.close - Method to close the tab.
   * @param {Function} tab.toggle - Method to toggle the tab's open state.
   * @param {Function} tab.isOpen - Method to check if the tab is currently open.
   * @returns {Promise<void>} Resolves when the tab switching is complete.
   */
  async changeTabs(tab) {
    if (this.currentTab && this.currentTab !== tab) {
      await this.currentTab.close(this.animationType);
    }
    await tab.toggle(!tab.isOpen, this.animationType);
    tab.isOpen && (this.currentTab = tab);
  }
}
